import React, { useState } from 'react';
import Modal from 'react-modal';
import { X } from 'lucide-react';
import PropTypes from 'prop-types';

const ReorderModal = ({
  isOpen,
  onClose,
  onReorder,
  waypoints,
  currentPosition,
  placeId
}) => {
  const [confirmationData, setConfirmationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Styles convertis en objets JavaScript
  const styles = {
    modal: {
      overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        fontFamily: 'Montserrat, sans-serif',
      },
      content: {
        position: 'relative',
        top: 'auto',
        left: 'auto',
        right: 'auto',
        bottom: 'auto',
        padding: '24px',
        width: '90%',
        maxWidth: '400px',
        border: 'none',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
        fontFamily: 'Montserrat, sans-serif',
        borderRadius: 19,
      }
    },
    modalContainer: {
      position: 'relative'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem'
    },
    modalText: {
      marginBottom: '1rem',
      color: '#6b7280'
    },
    modalList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      maxHeight: '50vh',
      overflowY: 'auto'
    },
    modalItem: {
      width: '100%',
      padding: '0.75rem',
      textAlign: 'left',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      background: 'white',
      transition: 'all 0.2s',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      border: 'none'
    },
    modalItemHover: {
      borderColor: '#93c5fd',
      backgroundColor: '#f0f9ff'
    },
    modalItemDisabled: {
      backgroundColor: '#f3f4f6',
      cursor: 'not-allowed'
    },
    modalItemCurrent: {
      borderColor: '#EBF8FF',
      backgroundColor: '#EBF8FF'
    },
    modalItemContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      width: '100%'
    },
    positionBadge: {
      width: '2rem',
      height: '2rem',
      borderRadius: '9999px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 500,
      flexShrink: 0
    },
    normalBadge: {
      backgroundColor: '#51C7F2',
      color: 'white'
    },
    depotBadge: {
      backgroundColor: '#CBD5E0',
      color: 'white'
    },
    addressText: {
      margin: 0,
      fontSize: '0.875rem',
      color: '#1f2937',
      textAlign: 'left',
      flexGrow: 1
    },
    depotText: {
      color: '#6b7280'
    },
    depotLabel: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginTop: '0.25rem'
    },
    currentLabel: {
      backgroundColor: '#51C7F2',
      borderRadius: '9999px',
      padding: '4px 8px',
      fontSize: '0.75rem',
      color: '#fff',
      fontWeight: 500,
      marginLeft: 'auto',
      flexShrink: 0
    },
    confirmationContent: {
      textAlign: 'center',
      padding: '1rem 0'
    },
    confirmationText: {
      marginBottom: '1rem',
      color: '#4B5563',
      lineHeight: 1.5
    },
    confirmationWarning: {
      color: '#F59E0B',
      fontWeight: 500,
      marginBottom: '2rem'
    },
    closeButton: {
      padding: '0.25rem',
      borderRadius: '50%',
      transition: 'background-color 0.2s',
      lineHeight: 0,
      background: 'none',
      border: 'none',
      cursor: 'pointer'
    },
    closeButtonHover: {
      backgroundColor: '#F7FAFC'
    },
    confirmationButtons: {
      width: '100%',
      fontFamily: 'Montserrat',
      paddingLeft: '1rem',
      paddingRight: '1rem',
      marginBottom: '5px',
      marginTop: '1rem',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    confirmButton: {
      width: '150px',
      height: '50px',
      background: '#5BBCEA',
      border: 'none',
      cursor: 'pointer',
      color: 'white',
      borderRadius: 7,
      marginTop: 5,
      fontFamily: 'Montserrat',
      marginRight: 10,
      fontWeight: 'bold',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    cancelButton: {
      width: '150px',
      height: '50px',
      background: 'red',
      border: 'none',
      cursor: 'pointer',
      color: 'white',
      borderRadius: 7,
      marginTop: 5,
      fontFamily: 'Montserrat',
      fontWeight: 'bold',
    }
  };

  const handlePositionSelect = (newPosition, waypoint) => {
    setConfirmationData({
      currentPosition,
      newPosition,
      waypoint,
      currentWaypoint: waypoints.find(wp => wp.position === currentPosition && wp.adresse.value.place_id === placeId)
    });
  };

  const confirmReorder = async () => {
    try {
      setIsLoading(true);
      await onReorder(currentPosition, placeId, confirmationData.newPosition);
      setConfirmationData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelReorder = () => {
    setConfirmationData(null);
  };

  return (
    <>
      {/* Modal principal de sélection de position */}
      <Modal
        isOpen={isOpen && !confirmationData}
        onRequestClose={onClose}
        style={styles.modal}
        ariaHideApp={false}
      >
        <div style={styles.modalContainer}>
          <div style={styles.modalHeader}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1f2937' }}>Déplacer le point de passage</h2>
            <button 
              onClick={onClose}
              style={styles.closeButton}
              aria-label="Fermer"
            >
              <X size={20} color="#666" />
            </button>
          </div>
          
          <p style={styles.modalText}>Sélectionnez la nouvelle position</p>
          
          <div style={styles.modalList}>
            {waypoints.map((waypoint) => {
              const isDepot = waypoint.depot;
              const isCurrent = waypoint.position === currentPosition && placeId === waypoint.adresse.value.place_id;
              
              return (
                <button
                  key={waypoint.adresse.value.place_id}
                  style={{
                    ...styles.modalItem,
                    ...(isDepot ? {} : {}),
                    ...(isCurrent ? styles.modalItemCurrent : {})
                  }}
                  onClick={() => !isDepot && !isCurrent && handlePositionSelect(waypoint.position, waypoint)}
                  disabled={isDepot || isCurrent}
                >
                  <div style={styles.modalItemContent}>
                    <div style={{
                      ...styles.positionBadge,
                      ...(isDepot ? styles.depotBadge : styles.normalBadge)
                    }}>
                      {waypoint.position}
                    </div>
                    <div>
                      <p style={{
                        ...styles.addressText,
                        ...(isDepot ? styles.depotText : {})
                      }}>
                        {waypoint.adresse.label}
                      </p>
                      {isDepot && (
                        <span style={styles.depotLabel}>Dépôt</span>
                      )}
                    </div>
                   
                    {isCurrent && (
                      <span style={styles.currentLabel}>
                        Position actuelle
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </Modal>

      {/* Modal de confirmation */}
      <Modal
        isOpen={!!confirmationData}
        onRequestClose={cancelReorder}
        style={styles.modal}
        ariaHideApp={false}
      >
        <div style={styles.modalContainer}>
          <div style={{
            width: '20rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}>
            <h2 style={{
              fontWeight: 'bold',
              fontFamily: 'Montserrat',
              fontSize: 20,
              color: '#51C7F2',
            }}>Confirmer le déplacement</h2>
          </div>
          
          <div style={styles.confirmationContent}>
            
            <p style={styles.confirmationText}>
              Vous allez déplacer le point <strong>{confirmationData?.currentWaypoint?.adresse.label}</strong> 
              de la position <strong>{confirmationData?.currentPosition}</strong> à la position <strong>{confirmationData?.newPosition}</strong>.
            </p>
            
            <p style={styles.confirmationWarning}>
              Cette action modifiera l'ordre de tous les points suivants.
            </p>
            
            <div style={styles.confirmationButtons}>
              <button
                onClick={confirmReorder}
                disabled={isLoading}
                style={styles.confirmButton}
              >
                {isLoading ? (
                  <div className="loader"></div>
                ) : (
                  'Confirmer'
                )}
              </button>
              <button
                onClick={cancelReorder}
                disabled={isLoading}
                style={styles.cancelButton}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

ReorderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  currentPosition: PropTypes.number,
  waypoints: PropTypes.array.isRequired,
  placeId: PropTypes.string
}; 

export default ReorderModal;